import React, { useState, useEffect } from 'react';
import { Box, Paper, Stack, Button, Typography } from '@mui/material';
import { Person, StringType, Stringing, PersonFormData, Racquet, Material } from '../types';
import {
  getAllPersons,
  getAllRacquets,
  getAllStringTypes,
  createPerson,
  createRacquet,
  createString,
  createStringing,
  updateStringing,
} from '../api';

import StepPersonForm from './StepPersonForm';
import StepRacquetForm from './StepRacquetForm';
import StepMainsForm from './StepMainsForm';
import StepCrossesForm from './StepCrossesForm';
import StepDetailsForm from './StepDetailsForm';
import CreateStringTypeForm from './CreateStringTypeForm';
import FormSummary from './FormSummary'

type Step = 'person' | 'racquet' | 'mains' | 'crosses' | 'details';

interface Props {
  stringing?: Stringing;
  persons: Person[];
  stringTypes: StringType[];
  onSave: () => void;
  onCancel: () => void;
  title: string;
}

const DEFAULT_NEW_STRING_DATA: StringType = {
  id: '',
  brand: '',
  name: '',
  gauge: '',
  color: '',
  material: Material.POLYESTER,
  stiffness: undefined,
  spinPotential: undefined,
};

export default function CreateStringingForm({ onSave, onCancel, stringing, title }: Props) {
  const [step, setStep] = useState<Step>('person');

  const [persons, setPersons] = useState<Person[]>([]);
  const [stringTypes, setStringTypes] = useState<StringType[]>([]);

  const [personId, setPersonId] = useState('');
  const [newPersonData, setNewPersonData] = useState<PersonFormData>({ firstName: '', lastName: '', preferences: undefined });
  const [isAddingPerson, setIsAddingPerson] = useState(false);

  const [mainsId, setMainsId] = useState('');
  const [newStringTypeData, setNewStringTypeData] = useState<StringType>(DEFAULT_NEW_STRING_DATA);
  const [isAddingString, setIsAddingString] = useState(false);
  const [isAddingCrossString, setIsAddingCrossString] = useState(false);
  const [newCrossStringData, setNewCrossStringData] = useState<StringType>(DEFAULT_NEW_STRING_DATA);

  const [crossesId, setCrossesId] = useState('');
  const [mainsTension, setMainsTension] = useState<number | null>(null);
  const [crossesTension, setCrossesTension] = useState<number | null>(null);

  const [date, setDate] = useState(() => {
    if (stringing?.strungAt) {
      return new Date(stringing.strungAt).toISOString().slice(0, 10);
    }
    return new Date().toISOString().slice(0, 10); // ← today by default
  });
  const [feedback, setFeedback] = useState('');

  const [isCrossesSameAsMains, setIsCrossesSameAsMains] = useState(false);

  const [racquets, setRacquets] = useState<Racquet[]>([]);
  const [racquetId, setRacquetId] = useState('');
  const [isAddingRacquet, setIsAddingRacquet] = useState(false);
  const [newRacquetData, setNewRacquetData] = useState({
    brand: '',
    model: '',
  });

  useEffect(() => {
    if (stringing) {
      setPersonId(stringing.personId);
      setRacquetId(stringing.racquetId);
      setMainsId(stringing.mainsId);
      setCrossesId(stringing.crossesId || '');
      setMainsTension(stringing.mainsTension);
      setCrossesTension(stringing.crossesTension ?? null);
      setDate(
        stringing.strungAt
          ? new Date(stringing.strungAt).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10)
      );
      setFeedback(stringing.feedback || '');

      // ⭐ Automatically jump to 'details' if editing
      setStep('details');
    }
  }, [stringing]);


  useEffect(() => {
    getAllPersons({ sortBy: 'recent' }).then(setPersons).catch(console.error);
    getAllStringTypes().then(setStringTypes).catch(console.error);
    getAllRacquets().then(setRacquets).catch(console.error); // NEW
  }, []);


  const handleNextFromPerson = async () => {
    if (isAddingPerson) {
      if (!newPersonData.firstName || !newPersonData.lastName) {
        alert('Please fill in the new person\'s name.');
        return;
      }
      try {
        const created = await createPerson(newPersonData);
        setPersonId(created.id);
        const updated = await getAllPersons({ sortBy: 'recent' });
        setPersons(updated);
        setIsAddingPerson(false);
        setStep('racquet');
      } catch (err) {
        console.error(err);
        alert('Failed to create person.');
      }
    } else if (personId) {
      setStep('racquet');
    } else {
      alert('Please select a person.');
    }
  };

  const handleNextFromRacquet = async () => {
    if (isAddingRacquet) {
      if (!newRacquetData.brand || !newRacquetData.model) {
        alert('Please fill in racquet brand and model.');
        return;
      }

      try {
        const created = await createRacquet(newRacquetData);
        setRacquetId(created.id);
        const updated = await getAllRacquets();
        setRacquets(updated);
        setIsAddingRacquet(false);
        setStep('mains');
      } catch (err) {
        console.error(err);
        alert('Failed to create racquet.');
      }
    } else if (racquetId) {
      setStep('mains');
    } else {
      alert('Please select a racquet.');
    }
  };


  const handleNextFromMains = async () => {
    if (isAddingString) {
      if (!newStringTypeData.brand || !newStringTypeData.name) {
        alert('Please fill in string details.');
        return;
      }
      try {
        const created = await createString(newStringTypeData);
        setMainsId(created.id);
        const updated = await getAllStringTypes();
        setStringTypes(updated);
        setIsAddingString(false);
        setStep('crosses');
      } catch (err) {
        console.error(err);
        alert('Failed to create string.');
      }
    } else if (mainsId) {
      if (mainsId) {
        if (isCrossesSameAsMains) {
          setCrossesId(mainsId);
          setCrossesTension(mainsTension);
          setStep('details');
        } else {
          setStep('crosses');
        }
      }
    } else {
      alert('Please select a mains string.');
    }
  };

  const handleSubmit = async () => {
    try {
      if (
        !personId ||
        !mainsId ||
        mainsTension === null ||
        !date
      ) {
        alert('Please complete all required fields.');
        return;
      }

      let finalCrossesId = crossesId;

      if (isAddingCrossString) {
        if (!newCrossStringData.brand || !newCrossStringData.name) {
          alert('Please fill in the new crosses string details.');
          return;
        }
        const createdCrossString = await createString(newCrossStringData);
        finalCrossesId = createdCrossString.id;

        const updated = await getAllStringTypes();
        setStringTypes(updated);
        setIsAddingCrossString(false);
      }

      const payload = {
        personId,
        racquetId,
        mainsId,
        crossesId: finalCrossesId || null,
        mainsTension,
        crossesTension: crossesTension ?? null,
        strungAt: new Date(date + 'T00:00:00Z'),
        feedback,
      };


      const res = stringing?.id
        ? await updateStringing({ id: stringing.id, ...payload })
        : await createStringing(payload);

      if (res) {
        onSave();
      } else {
        alert('Failed to save stringing.');
      }
    } catch (err) {
      console.error(err);
      alert('An error occurred while submitting.');
    }
  };

  return (
    <>
      <FormSummary
        person={step !== 'person' ? persons.find(p => p.id === personId) : undefined}
        racquet={(step !== 'person' && step !== 'racquet') ? racquets.find(r => r.id === racquetId) : undefined}
        mains={(step !== 'person' && step !== 'racquet' && step !== 'mains') ? stringTypes.find(s => s.id === mainsId) : undefined}
        mainsTension={(step !== 'person' && step !== 'racquet' && step !== 'mains') ? mainsTension : undefined}
        crosses={(step === 'details') ? stringTypes.find(s => s.id === crossesId) : undefined}
        crossesTension={(step === 'details') ? crossesTension : undefined}
        date={(step === 'details') ? date : undefined}
        feedback={(step === 'details') ? feedback : undefined}
        onEditStep={setStep}
      />

      {/* PERSON STEP */}
      {step === 'person' && (
        <StepPersonForm
          persons={persons}
          personId={personId}
          onSelectPerson={(id) => {
            if (id === 'add-person') {
              setIsAddingPerson(true);
              setPersonId('');
            } else {
              setPersonId(id);
              setIsAddingPerson(false);
              setStep('racquet');
            }
          }}
          isAddingPerson={isAddingPerson}
          onAddPersonToggle={setIsAddingPerson}
          newPersonData={newPersonData}
          onChangeNewPerson={setNewPersonData}
        />
      )}

      {/* RACQUET STEP */}
      {step === 'racquet' && (
        <StepRacquetForm
          racquets={racquets}
          racquetId={racquetId}
          onSelectRacquet={(id) => {
            if (id === 'add-racquet') {
              setIsAddingRacquet(true);
              setRacquetId('');
            } else {
              setIsAddingRacquet(false);
              setRacquetId(id);
              setStep('mains');
            }
          }}
          isAddingRacquet={isAddingRacquet}
          onAddRacquetToggle={setIsAddingRacquet}
          newRacquetData={newRacquetData}
          onChangeNewRacquet={setNewRacquetData}
        />
      )}

      {/* MAINS STEP */}
      {step === 'mains' && (
        <>
          <StepMainsForm
            stringTypes={stringTypes}
            mainsId={mainsId}
            onSelectMains={(id) => {
              if (id === 'add-string') {
                setIsAddingString(true);
              } else {
                setMainsId(id);
              }
            }}
            mainsTension={mainsTension}
            onChangeMainsTension={setMainsTension}
            isCrossesSameAsMains={isCrossesSameAsMains}
            onChangeCrossesSameAsMains={setIsCrossesSameAsMains}
          />
          {isAddingString && (
            <CreateStringTypeForm
              onSave={async (data) => {
                const created = await createString(data);
                setStringTypes(await getAllStringTypes());
                setMainsId(created.id);
                setIsAddingString(false);
              }}
              onCancel={() => setIsAddingString(false)}
            />
          )}
        </>
      )}

      {/* CROSSES STEP */}
      {step === 'crosses' && (
        <>
          <StepCrossesForm
            stringTypes={stringTypes}
            crossesId={crossesId}
            onSelectCrosses={(id) => {
              if (id === 'add-string') {
                setIsAddingCrossString(true);
              } else {
                setCrossesId(id);
              }
            }}
            crossesTension={crossesTension}
            onChangeCrossesTension={setCrossesTension}
          />
          {isAddingCrossString && (
            <CreateStringTypeForm
              onSave={async (data) => {
                const created = await createString(data);
                setStringTypes(await getAllStringTypes());
                setCrossesId(created.id);
                setIsAddingCrossString(false);
              }}
              onCancel={() => setIsAddingCrossString(false)}
            />
          )}
        </>
      )}

      {/* DETAILS STEP */}
      {step === 'details' && (
        <StepDetailsForm
          date={date}
          feedback={feedback}
          onChangeDate={setDate}
          onChangeFeedback={setFeedback}
        />
      )}

      {/* BUTTONS */}
      <Stack direction="row" justifyContent="space-between" mt={3}>
        <Button variant="text" onClick={onCancel}>Cancel</Button>

        {step === 'person' && <Button variant="contained" onClick={handleNextFromPerson}>Next</Button>}
        {step === 'racquet' && <Button variant="contained" onClick={handleNextFromRacquet}>Next</Button>}
        {step === 'mains' && <Button variant="contained" onClick={handleNextFromMains}>Next</Button>}
        {step === 'crosses' && <Button variant="contained" onClick={() => setStep('details')}>Next</Button>}
        {step === 'details' && <Button variant="contained" onClick={handleSubmit}>Submit</Button>}
      </Stack>
    </>
  );
}
